import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";

// Secure random ID generator for environments without crypto.randomUUID
function secureRandomId(): string {
  if (typeof globalThis.crypto !== "undefined" && typeof globalThis.crypto.getRandomValues === "function") {
    // Browser
    const arr = new Uint8Array(16); // 128 bits
    globalThis.crypto.getRandomValues(arr);
    return Array.from(arr).map(b => b.toString(16).padStart(2, "0")).join("");
  } else {
    // Node.js or other
    try {
      // Dynamically require to avoid issues if in browser
      const nodeCrypto = require("crypto");
      if (typeof nodeCrypto.randomUUID === "function") {
        return nodeCrypto.randomUUID();
      }
      return nodeCrypto.randomBytes(16).toString("hex");
    } catch (e) {
      // As last resort, throw
      throw new Error("No secure random source available for session ID.");
    }
  }
}
import { processRawData } from "../utils";
import { generateSecureId } from "@/utils/secureRandom";
import { extractAssistantTextFromJson } from "@/utils/meetingNotesChatSse";
import {
  extractMeetingIdsFromHistoryForSession,
  extractMeetingIdsFromSessionHistoryPayload,
  filterAndSortMeetingHistoryForSession,
  getMeetingHistoryAssistantText,
  getMeetingHistoryMeetingName,
  getMeetingHistoryUserQuery,
  normalizeMeetingChatHistoryEnvelope,
} from "@/utils/meetingChatHistorySession";
import { tryActivateSiteDisabled403 } from "@/utils/siteDisabled403";
import {
  getSessionMeetingEntry,
  persistSessionMeetingEntry,
} from "@/utils/sessionMeetingMap";
import {
  collectMeetingIdsFromThreads,
  isMeetingAssistThread,
  parseSessionRowMeetingMetadata,
  persistThreadMeetingLink,
  resolveMeetingIdsForSession,
} from "@/utils/teamsMeetingAssistSession";
import {
  addMessage,
  appendToBotMessage,
  markMessageComplete,
  setDocumentSearchResults,
  setFollowUpQuestions,
  setSessionId,
  type FollowUpQuestion,
  migrateSessionMessages,
  setThreads,
  setSessionMessages,
  renameThread,
  setSessionCollections,
  mergeThreadMeetingMetadata,
  showPopup,
  ChatThread,
  ChatMessage,
} from "../reducers/chatSlice";
import type { AppDispatch } from "../store";
import {
  chatUnifiedEndpointApiUrl,
  chatMeetingNotesChatApiUrl,
  fetchChatHistoryFromSession,
  getMeetingChatHistoryUrl,
  getThreadHistoryUrl,
  collectionChatApiUrl,
  employee360ApiUrl,
} from "../api";
import {
  getClientAuthHeaders,
  getClientFetchInit,
} from "@/lib/browserBackend";

// --- Safe coercion helpers ---
type LlmObj =
  | {
      response?: any;
      results?: any;
      content?: any;
      answer?: any;
      message?: any;
      text?: any;
      llm_response?: any;
      reasoning?: any;
      chat_image?: any;
    }
  | string
  | null
  | undefined;

type CoercedLlm = {
  responseText: string;
  reasoning: string | undefined;
  images: any;
};

const ensureString = (val: any, fallback = ""): string => {
  if (typeof val === "string") return val;
  if (
    val &&
    typeof val === "object" &&
    typeof (val as any).response === "string"
  ) {
    return (val as any).response;
  }
  if (val == null) return fallback;
  try {
    return JSON.stringify(val);
  } catch {
    return fallback;
  }
};

const coerceLlm = (raw: LlmObj): CoercedLlm => {
  if (raw == null) {
    return {
      responseText: "",
      reasoning: undefined,
      images: undefined as any,
    };
  }
  if (typeof raw === "string") {
    // Could be JSON or plain text
    try {
      const obj = JSON.parse(raw);
      const candidate =
        (obj as any).response ??
        (obj as any).results ??
        (obj as any).content ??
        (obj as any).answer ??
        (obj as any).message ??
        (obj as any).text ??
        (obj as any).llm_response;
      if (candidate != null && candidate !== obj) {
        const nested: CoercedLlm = coerceLlm(candidate);
        return {
          responseText: nested.responseText || ensureString(candidate, raw),
          reasoning: (obj as any).reasoning ?? nested.reasoning,
          images: (obj as any).chat_image ?? nested.images,
        };
      }
      return {
        responseText: ensureString(
          candidate,
          raw
        ),
        reasoning: (obj as any).reasoning,
        images: (obj as any).chat_image,
      };
    } catch {
      return { responseText: raw, reasoning: undefined, images: undefined };
    }
  }
  // Already an object (common for collection_chat)
  const candidate =
    (raw as any).response ??
    (raw as any).results ??
    (raw as any).content ??
    (raw as any).answer ??
    (raw as any).message ??
    (raw as any).text ??
    (raw as any).llm_response;
  const nested: CoercedLlm | null =
    candidate != null && candidate !== raw ? coerceLlm(candidate) : null;
  return {
    responseText:
      nested?.responseText ?? (candidate != null ? ensureString(candidate) : ""),
    reasoning: (raw as any).reasoning ?? nested?.reasoning,
    images: (raw as any).chat_image ?? nested?.images,
  };
};

function normalizeFollowUpQuestions(questions: unknown): FollowUpQuestion[] {
  if (!Array.isArray(questions)) return [];
  return questions
    .map((q): FollowUpQuestion | null => {
      if (!q || typeof q !== "object") return null;
      const o = q as Record<string, unknown>;
      const text = typeof o.text === "string" ? o.text.trim() : "";
      const target_agent =
        typeof o.target_agent === "string" ? o.target_agent.trim() : "";
      if (!text || !target_agent) return null;
      return {
        text,
        category: typeof o.category === "string" ? o.category : "",
        target_agent,
        confidence:
          typeof o.confidence === "number" && Number.isFinite(o.confidence)
            ? o.confidence
            : 0,
        grounding_source: Array.isArray(o.grounding_source)
          ? o.grounding_source.map((s) => String(s))
          : [],
      };
    })
    .filter((item): item is FollowUpQuestion => item != null);
}

/** Pull follow-up chips from history interaction payloads (several backend shapes). */
function extractFollowUpQuestionsFromInteraction(
  interaction: Record<string, unknown> | null | undefined
): FollowUpQuestion[] {
  if (!interaction || typeof interaction !== "object") return [];
  const candidates = [
    interaction.followup_questions,
    interaction.follow_up_questions,
    interaction.followUpQuestions,
    (interaction.response as Record<string, unknown> | undefined)
      ?.followup_questions,
    (interaction.response as Record<string, unknown> | undefined)
      ?.follow_up_questions,
    (interaction.data as Record<string, unknown> | undefined)?.followup_questions,
    (
      (interaction.data as Record<string, unknown> | undefined)?.qna as
        | Record<string, unknown>
        | undefined
    )?.followup_questions,
    (
      (interaction.data as Record<string, unknown> | undefined)?.qna as
        | Record<string, unknown>
        | undefined
    )?.response &&
      (
        (
          (interaction.data as Record<string, unknown>).qna as Record<
            string,
            unknown
          >
        ).response as Record<string, unknown>
      )?.followup_questions,
    (interaction.metadata as Record<string, unknown> | undefined)
      ?.followup_questions,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeFollowUpQuestions(candidate);
    if (normalized.length > 0) return normalized;
  }
  return [];
}

function sessionMessagesIndicateMeetingAssist(
  messages: ChatMessage[] | undefined
): boolean {
  if (!Array.isArray(messages) || messages.length === 0) return false;
  return messages.some((m) => {
    const t = m?.type;
    if (typeof t !== "string" || !t) return false;
    return (
      t === "meeting_notes" ||
      t === "meeting_notes_chat" ||
      t.includes("meeting_notes")
    );
  });
}

function isTeamsMeetingAssistChat(
  payload: any,
  thread: ChatThread | undefined,
  sessionMessages: ChatMessage[] | undefined
): boolean {
  const pt = payload?.type;
  if (pt === "meeting_notes" || pt === "meeting_notes_chat") return true;
  if (payload?.source === "teams_ai_assist") return true;
  if (
    Array.isArray(payload?.meeting_ids) &&
    payload.meeting_ids.length > 0 &&
    String(payload.meeting_ids[0]).trim() !== ""
  ) {
    return true;
  }
  if (
    typeof payload?.meeting_id === "string" &&
    payload.meeting_id.trim() !== ""
  ) {
    return true;
  }
  if (sessionMessagesIndicateMeetingAssist(sessionMessages)) return true;
  if (!thread) return false;
  if (thread.chatType === "teams_ai_assist") return true;
  const tt = thread.type;
  if (tt === "meeting_notes" || tt === "meeting_notes_chat") return true;
  if (thread.source === "teams_ai_assist") return true;
  const mid = thread.meeting_id;
  if (mid != null && String(mid).trim() !== "") return true;
  return false;
}

function normalizeMeetingIdsList(ids: unknown[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const x of ids) {
    const s = String(x).trim();
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
}

function extractMeetingIdsFromInteractions(interactions: unknown[]): string[] {
  if (!Array.isArray(interactions)) return [];
  for (const inter of interactions) {
    if (!inter || typeof inter !== "object") continue;
    const rec = inter as Record<string, unknown>;
    const nests = [rec, rec.data, rec.response, rec.metadata].filter(
      (x): x is Record<string, unknown> =>
        x != null && typeof x === "object" && !Array.isArray(x)
    );
    for (const n of nests) {
      const mids = n.meeting_ids;
      if (Array.isArray(mids) && mids.length > 0) {
        const normalized = normalizeMeetingIdsList(mids);
        if (normalized.length > 0) return normalized;
      }
      const mid = n.meeting_id;
      if (mid != null && String(mid).trim() !== "") {
        return [String(mid).trim()];
      }
    }
  }
  return [];
}

/** Match sessions API thread row when Redux map key differs from URL only by UUID casing. */
function getThreadFromStoreBySessionId(
  threads: Record<string, ChatThread>,
  sessionId: string
): ChatThread | undefined {
  const trimmed = sessionId.trim();
  if (!trimmed) return undefined;
  if (threads[trimmed]) return threads[trimmed];
  const lower = trimmed.toLowerCase();
  for (const t of Object.values(threads)) {
    const sid = t?.session_id;
    if (sid != null && String(sid).trim().toLowerCase() === lower) return t;
  }
  return undefined;
}

function getMessagesForSessionLoose(
  messages: Record<string, ChatMessage[]>,
  sessionId: string
): ChatMessage[] | undefined {
  const t = sessionId.trim();
  if (!t) return undefined;
  if (Object.prototype.hasOwnProperty.call(messages, t)) return messages[t];
  const lower = t.toLowerCase();
  for (const [k, v] of Object.entries(messages)) {
    if (k.toLowerCase() === lower && Array.isArray(v)) return v;
  }
  return undefined;
}

function messageCountForSession(
  messages: Record<string, ChatMessage[]>,
  sessionId: string
): number {
  return getMessagesForSessionLoose(messages, sessionId)?.length ?? 0;
}

function seedChatTurnIfNeeded(
  dispatch: (action: ReturnType<typeof addMessage>) => unknown,
  messages: Record<string, ChatMessage[]>,
  {
    userKey,
    botKey,
    sessionId,
    userInput,
    userExtras = {},
    botExtras = {},
  }: {
    userKey: string;
    botKey: string;
    sessionId: string;
    userInput: string;
    userExtras?: Record<string, any>;
    botExtras?: Record<string, any>;
  }
): boolean {
  const existingForSession =
    getMessagesForSessionLoose(messages, sessionId) ?? [];
  const hasUserByKey = existingForSession.some(
    (m) => m.key === userKey && m.role === "user"
  );
  const hasBotByKey = existingForSession.some(
    (m) => m.key === botKey && m.role === "bot"
  );
  const hasSameOpenTurn = existingForSession.some((m, index) => {
    if (m.role !== "user" || m.question !== userInput) return false;
    return existingForSession.slice(index + 1).some(
      (next) =>
        next.role === "bot" &&
        !next.complete &&
        !(next.question || next.content || next.greeting)
    );
  });

  if ((hasUserByKey && hasBotByKey) || hasSameOpenTurn) {
    return false;
  }

  dispatch(
    addMessage({
      key: userKey,
      role: "user",
      question: userInput,
      thought: "",
      sessionId,
      ...userExtras,
    })
  );
  dispatch(
    addMessage({
      key: botKey,
      role: "bot",
      greeting: "",
      question: "",
      thought: "",
      sessionId,
      ...botExtras,
    })
  );
  return true;
}

function getStoredCollectionFileItems(sessionId?: string): { id: string; name: string }[] {
  if (typeof sessionStorage === "undefined" || !sessionId) return [];
  try {
    const raw = sessionStorage.getItem(`chat_selectedFileItems:${sessionId}`);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => ({
        id: String(item?.id ?? "").trim(),
        name: String(item?.name ?? item?.id ?? "").trim(),
      }))
      .filter((item) => item.id && item.name);
  } catch {
    return [];
  }
}

function getStoredCollectionFileIds(sessionId?: string): string[] {
  if (typeof sessionStorage === "undefined" || !sessionId) return [];
  try {
    const scoped = sessionStorage.getItem(`chat_selectedRows:${sessionId}`);
    const global = sessionStorage.getItem("chat_selectedRows");
    const raw = scoped ?? global;
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((id) => String(id).trim())
      .filter((id) => id.length > 0);
  } catch {
    return [];
  }
}

type CollectionChatSnapshot = {
  displayNames: string[];
  label: "Files" | "Collections";
  contextNames?: string[];
};

function resolveCollectionChatSnapshot(
  sessionId: string,
  state: RootState["chat"]
): CollectionChatSnapshot {
  const fileItemsFromRedux = state.sessionCollectionFileItems?.[sessionId] ?? [];
  const fileItemsFromStorage = getStoredCollectionFileItems(sessionId);
  const fileItems =
    fileItemsFromRedux.length > 0 ? fileItemsFromRedux : fileItemsFromStorage;

  const fileIdsFromRedux = state.sessionCollectionFileIds?.[sessionId] ?? [];
  const fileIdsFromStorage = getStoredCollectionFileIds(sessionId);
  const fileIds =
    fileIdsFromRedux.length > 0 ? fileIdsFromRedux : fileIdsFromStorage;

  const contextNames = (state.sessionCollections?.[sessionId] ?? [])
    .map((c) => String(c.name ?? c.id ?? "").trim())
    .filter(Boolean);

  const hasFileSelection = fileItems.length > 0 || fileIds.length > 0;

  if (hasFileSelection) {
    const displayNames =
      fileItems.length > 0
        ? fileItems.map((f) => String(f.name ?? f.id).trim()).filter(Boolean)
        : fileIds;
    return {
      displayNames,
      label: "Files",
      contextNames: contextNames.length > 0 ? contextNames : undefined,
    };
  }

  return {
    displayNames: contextNames,
    label: "Collections",
  };
}

const EMPTY_BOT_FALLBACKS = new Set([
  "",
  "I don't have anything to show yet.",
  "No response available.",
]);

function getBotDisplayText(message?: ChatMessage): string {
  if (!message || message.role !== "bot") return "";
  const raw =
    typeof message.question === "string" && message.question.trim()
      ? message.question
      : typeof message.content === "string" && message.content.trim()
        ? message.content
        : typeof message.greeting === "string" && message.greeting.trim()
          ? message.greeting
          : "";
  return raw.trim();
}

function shouldPreserveLocalMessages(
  existing: ChatMessage[] | undefined,
  incoming: ChatMessage[]
): boolean {
  if (!Array.isArray(existing) || existing.length === 0) return false;
  const existingLastBot = [...existing].reverse().find((m) => m.role === "bot");
  const incomingLastBot = [...incoming].reverse().find((m) => m.role === "bot");
  const existingText = getBotDisplayText(existingLastBot);
  const incomingText = getBotDisplayText(incomingLastBot);
  const incomingIsEmpty = EMPTY_BOT_FALLBACKS.has(incomingText);
  const existingHasUsefulText =
    existingText.length > 0 && !EMPTY_BOT_FALLBACKS.has(existingText);

  if (existingLastBot && !existingLastBot.complete && incomingIsEmpty) {
    return true;
  }
  if (existingHasUsefulText && incomingIsEmpty) {
    return true;
  }
  return false;
}

/** Keep the address bar aligned with the active chat session id. */
function syncHomeUrlToSessionId(sessionId: string) {
  if (typeof window === "undefined") return;
  const id = sessionId.trim();
  if (!id || !isValidUUID(id)) return;
  const pathParts = window.location.pathname.split("/");
  if (pathParts.length < 2 || pathParts[1] !== "home") return;
  const basePath = pathParts.slice(0, 2).join("/");
  if (pathParts[pathParts.length - 1] !== id) {
    window.history.replaceState(null, "", `${basePath}/${id}`);
  }
}

function getCurrentHomeRouteSessionId(): string {
  if (typeof window === "undefined") return "";
  const pathParts = window.location.pathname.split("/");
  if (pathParts[1] !== "home" || !pathParts[2]) return "";
  try {
    return decodeURIComponent(pathParts[2]).trim();
  } catch {
    return pathParts[2].trim();
  }
}

function shouldPromoteStreamingSession(
  getState: () => RootState,
  fromSessionId: string,
  toSessionId: string
): boolean {
  const currentStoreSessionId = String(getState().chat.sessionId ?? "")
    .trim()
    .toLowerCase();
  const currentRouteSessionId = getCurrentHomeRouteSessionId().toLowerCase();
  const matchesActiveSession = (id: string) => {
    const sid = String(id || "").trim().toLowerCase();
    return (
      Boolean(sid) &&
      (sid === currentStoreSessionId || sid === currentRouteSessionId)
    );
  };

  return matchesActiveSession(fromSessionId) || matchesActiveSession(toSessionId);
}

/** Resolve meeting_ids for POST /api/v1/meeting-notes/chat (thread row, payload, or threads map). */
function resolveMeetingIdsForNotesChat(
  payload: any,
  threads: Record<string, ChatThread>,
  threadLookupId: string
): string[] {
  const sid = threadLookupId.trim();
  const thread = sid ? getThreadFromStoreBySessionId(threads, sid) : undefined;

  if (thread?.meeting_ids && Array.isArray(thread.meeting_ids) && thread.meeting_ids.length > 0) {
    return normalizeMeetingIdsList(thread.meeting_ids);
  }
  if (thread?.meeting_id != null && String(thread.meeting_id).trim() !== "") {
    return [String(thread.meeting_id).trim()];
  }

  if (Array.isArray(payload?.meeting_ids) && payload.meeting_ids.length > 0) {
    return normalizeMeetingIdsList(payload.meeting_ids);
  }
  if (
    typeof payload?.meeting_id === "string" &&
    payload.meeting_id.trim() !== ""
  ) {
    return [payload.meeting_id.trim()];
  }

  if (
    typeof payload?.session_id === "string" &&
    payload.session_id.trim() !== ""
  ) {
    const alt = getThreadFromStoreBySessionId(threads, payload.session_id.trim());
    if (alt?.meeting_ids && Array.isArray(alt.meeting_ids) && alt.meeting_ids.length > 0) {
      return normalizeMeetingIdsList(alt.meeting_ids);
    }
    if (alt?.meeting_id != null && String(alt.meeting_id).trim() !== "") {
      return [String(alt.meeting_id).trim()];
    }
  }

  return [];
}

/* ---------------------- Threads list ---------------------- */
export const fetchChatThreads = createAsyncThunk<
  void,
  { accessToken: string; useremail: string },
  { state: RootState }
>(
  "chat/fetchChatThreads",
  async ({ accessToken, useremail }, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await axios.get(getThreadHistoryUrl(useremail), {
        headers: getClientAuthHeaders(accessToken, {
          "Content-Type": "application/json",
        }),
        withCredentials: true,
      });

      const data = response.data;

      let threads: Record<string, ChatThread> = {};
      if (Array.isArray(data.sessions)) {
        data.sessions.forEach((session: any) => {
          const sessionId = session.session_id;
          if (!sessionId) return;

          const meta = parseSessionRowMeetingMetadata(
            session && typeof session === "object"
              ? (session as Record<string, unknown>)
              : {}
          );

          const meetingIdsFromApi =
            meta.meeting_ids && meta.meeting_ids.length > 0
              ? meta.meeting_ids
              : meta.meeting_id
                ? [meta.meeting_id]
                : [];

          threads[sessionId] = {
            session_id: sessionId,
            session_name: session.session_name,
            is_active: session.is_active ?? false,
            ...(meetingIdsFromApi.length > 0 && { meeting_ids: meetingIdsFromApi }),
            ...(meta.meeting_id && { meeting_id: meta.meeting_id }),
            ...(meta.type && { type: meta.type }),
            ...(meta.source && { source: meta.source }),
            ...(meta.chatType && { chatType: meta.chatType }),
            ...(meta.meeting_name && { meeting_name: meta.meeting_name }),
          };

          if (meetingIdsFromApi.length > 0 || meta.chatType || meta.type || meta.source) {
            persistSessionMeetingEntry(sessionId, {
              ...(meta.meeting_id && { meeting_id: meta.meeting_id }),
              ...(meetingIdsFromApi.length > 0 && { meeting_ids: meetingIdsFromApi }),
              ...(meta.type && { type: meta.type }),
              ...(meta.source && { source: meta.source }),
              ...(meta.chatType && { chatType: meta.chatType }),
            });
          }
        });
      }

      /** Merge with existing threads so local metadata (e.g. meeting_id from Teams AI Assist) is not wiped when the sessions API omits it. */
      const prevThreads = getState().chat.threads;
      const merged: Record<string, ChatThread> = {};
      for (const [id, serverThread] of Object.entries(threads) as [
        string,
        ChatThread,
      ][]) {
        const prev = prevThreads[id];
        const serverMid =
          serverThread.meeting_id != null &&
          String(serverThread.meeting_id).trim() !== ""
            ? String(serverThread.meeting_id)
            : undefined;
        const serverMids =
          Array.isArray(serverThread.meeting_ids) && serverThread.meeting_ids.length > 0
            ? serverThread.meeting_ids
            : undefined;
        const serverMeetingNameTrimmed =
          typeof serverThread.meeting_name === "string" &&
          serverThread.meeting_name.trim() !== ""
            ? serverThread.meeting_name.trim()
            : undefined;

        merged[id] = {
          ...(prev || {}),
          ...serverThread,
          session_id: serverThread.session_id,
          session_name: serverThread.session_name,
          is_active: serverThread.is_active ?? prev?.is_active,
          meeting_ids: serverMids ?? prev?.meeting_ids,
          meeting_id:
            serverMid ??
            prev?.meeting_id ??
            (serverMids?.[0] != null ? String(serverMids[0]) : undefined) ??
            (prev?.meeting_ids?.[0] != null ? String(prev.meeting_ids[0]) : undefined),
          type: serverThread.type ?? prev?.type,
          source: serverThread.source ?? prev?.source,
          chatType: serverThread.chatType ?? prev?.chatType,
          meeting_name: serverMeetingNameTrimmed ?? prev?.meeting_name,
        };
      }

      /**
       * Sessions list can lag behind a brand-new Teams AI Assist / meeting-notes chat. If we only
       * keep API rows, the optimistic thread (with meeting_id) disappears — Recent Chats may
       * re-add a row without meeting_id and opening it skips meeting history (blank pane) until
       * the backend lists the session (often after a second turn). Only retain prior rows that
       * clearly belong to this flow or still have in-memory transcript.
       */
      const stateNow = getState().chat;
      const prevMessages = stateNow.messages || {};
      const activeSessionId =
        typeof stateNow.sessionId === "string" ? stateNow.sessionId.trim() : "";
      const pendingLocalThreads: Record<string, ChatThread> = {};
      for (const [id, prevThread] of Object.entries(prevThreads)) {
        if (merged[id] || !prevThread?.session_id) continue;
        const mapSessionId = String(id).trim();
        const sid = String(prevThread.session_id).trim();
        if (sid.startsWith("temp-")) {
          const bare = sid.slice("temp-".length);
          if (isValidUUID(bare) && merged[bare]) {
            if (Array.isArray(prevMessages[sid]) && prevMessages[sid].length > 0) {
              dispatch(
                migrateSessionMessages({ fromSessionId: sid, toSessionId: bare })
              );
            }
            continue;
          }
        }
        const hasLocalMessages =
          (Array.isArray(prevMessages[mapSessionId]) &&
            prevMessages[mapSessionId].length > 0) ||
          (Array.isArray(prevMessages[sid]) && prevMessages[sid].length > 0);
        const hasLocalMessageBucket =
          Object.prototype.hasOwnProperty.call(prevMessages, mapSessionId) ||
          Object.prototype.hasOwnProperty.call(prevMessages, sid);
        const isActiveLocalThread =
          Boolean(activeSessionId) &&
          (mapSessionId === activeSessionId || sid === activeSessionId);
        const isPendingUntitledThread =
          String(prevThread.session_name || "").trim() === "Untitled" &&
          (hasLocalMessageBucket || prevThread.is_active === true);
        const mid =
          prevThread.meeting_id != null && String(prevThread.meeting_id).trim() !== ""
            ? String(prevThread.meeting_id).trim()
            : "";
        const mids =
          Array.isArray(prevThread.meeting_ids) && prevThread.meeting_ids.length > 0;
        const meetingAssistMeta =
          Boolean(mid) ||
          mids ||
          prevThread.chatType === "teams_ai_assist" ||
          prevThread.source === "teams_ai_assist" ||
          prevThread.type === "meeting_notes_chat" ||
          prevThread.type === "meeting_notes";
        if (
          meetingAssistMeta ||
          hasLocalMessages ||
          isActiveLocalThread ||
          isPendingUntitledThread
        ) {
          const retainedThread = {
            ...prevThread,
            session_id: mapSessionId || sid,
          };
          if (isPendingUntitledThread) {
            pendingLocalThreads[id] = retainedThread;
          } else {
            merged[id] = retainedThread;
          }
        }
      }

      dispatch(setThreads({ ...pendingLocalThreads, ...merged }));
    } catch (err: any) {
      return rejectWithValue(err?.message || "Failed to fetch chat threads");
    }
  }
);

/* ---------------------- Default chat (existing) ---------------------- */
export const onTextChat = createAsyncThunk<
  void,
  {
    accessToken: string;
    payload: any;
    newChat: boolean;
    userInput: string;
    clientMessageKeys?: { userKey: string; botKey: string };
  },
  { state: RootState }
>(
  "chat/text-to-text-chat",
  async (
    { accessToken, payload, newChat, userInput, clientMessageKeys },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const payloadSessionTrim = normalizeChatSessionIdForApi(
        typeof payload?.session_id === "string" ? payload.session_id : ""
      );
      const payloadIsPersistedChat = Boolean(
        payloadSessionTrim && isValidUUID(payloadSessionTrim)
      );

      const currentSessionId = resolveChatSessionIdForSubmit({
        payloadSessionTrim,
        reduxSessionId: getState().chat.sessionId,
      });
      syncHomeUrlToSessionId(currentSessionId);
      if (getState().chat.sessionId !== currentSessionId) {
        dispatch(setSessionId(currentSessionId));
      }

      console.log("Session Debug:", {
        payloadSession: payload?.session_id,
        reduxSession: getState().chat.sessionId,
        finalSession: currentSessionId,
      });

      const threadLookupId =
        payloadSessionTrim || currentSessionId || "";
      const threadsMap = getState().chat.threads;
      const thread = threadLookupId
        ? getThreadFromStoreBySessionId(threadsMap, threadLookupId)
        : undefined;
      const sessionMessages = threadLookupId
        ? getMessagesForSessionLoose(getState().chat.messages, threadLookupId)
        : undefined;

      const isMeetingAssist = isTeamsMeetingAssistChat(
        payload,
        thread,
        sessionMessages
      );

      const meetingIdsResolved = resolveMeetingIdsForNotesChat(
        payload,
        threadsMap,
        threadLookupId
      );

      const lookupTrim = normalizeChatSessionIdForApi(threadLookupId);
      const sessionIdForMeetingNotes =
        lookupTrim && isValidUUID(lookupTrim)
          ? lookupTrim
          : payloadIsPersistedChat
            ? payloadSessionTrim
            : "";

      /**
       * Teams AI Assist / meeting-notes only: POST /api/v1/meeting-notes/chat with
       * { meeting_ids, query, session_id, type: "meeting_notes_chat" }. Unified chat must not use this path.
       */
      const canPostMeetingNotesChat =
        meetingIdsResolved.length > 0 &&
        Boolean(sessionIdForMeetingNotes) &&
        isMeetingAssist;

      if (canPostMeetingNotesChat && sessionIdForMeetingNotes) {
        persistSessionMeetingEntry(sessionIdForMeetingNotes, {
          meeting_id: meetingIdsResolved[0],
          meeting_ids: meetingIdsResolved,
          type: "meeting_notes_chat",
          source: "teams_ai_assist",
          chatType: "teams_ai_assist",
        });
      }

      const mustNotUseUnifiedForMeetingAssist =
        isMeetingAssist &&
        meetingIdsResolved.length === 0 &&
        (payload?.type === "meeting_notes" ||
          payload?.type === "meeting_notes_chat" ||
          payload?.source === "teams_ai_assist");

      if (mustNotUseUnifiedForMeetingAssist) {
        return rejectWithValue(
          "Meeting AI Assist is missing meeting_ids for this session. Refresh Recent Chats or reload the thread list, then try again."
        );
      }

      const queryText = String(
        payload?.query ?? userInput ?? ""
      ).trim();

      const url = canPostMeetingNotesChat
        ? chatMeetingNotesChatApiUrl
        : chatUnifiedEndpointApiUrl;

      const requestBody = canPostMeetingNotesChat
        ? {
            meeting_ids: meetingIdsResolved,
            query: queryText,
            session_id: sessionIdForMeetingNotes,
            type: "meeting_notes_chat",
          }
        : {
            ...payload,
            query: queryText,
            session_id: currentSessionId,
          };

      const userKey =
        clientMessageKeys?.userKey?.trim() || Date.now().toString();
      const botKey =
        clientMessageKeys?.botKey?.trim() || `${userKey}-bot`;

      const notesMsgType = canPostMeetingNotesChat ? "meeting_notes_chat" : undefined;
      const meetingNameForUserMsg =
        notesMsgType && thread?.meeting_name?.trim()
          ? thread.meeting_name.trim()
          : undefined;

      let streamingSessionId = currentSessionId;
      if (lookupTrim && isValidUUID(lookupTrim)) {
        streamingSessionId = lookupTrim;
        if (getState().chat.sessionId !== lookupTrim) {
          dispatch(setSessionId(lookupTrim));
        }
      }

      seedChatTurnIfNeeded(dispatch, getState().chat.messages, {
        userKey,
        botKey,
        sessionId: streamingSessionId,
        userInput,
        userExtras: {
          ...(notesMsgType && { type: notesMsgType }),
          ...(meetingNameForUserMsg && { meetingName: meetingNameForUserMsg }),
        },
        botExtras: {
          ...(notesMsgType && { type: notesMsgType }),
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 10));

      const isMeetingNotesStream = canPostMeetingNotesChat;

      console.log("API ROUTE:", {
        isMeetingAssist,
        canPostMeetingNotesChat,
        url,
        type: payload?.type,
      });

      const response = await fetch(
        url,
        getClientFetchInit(accessToken, {
          method: "POST",
          headers: getClientAuthHeaders(accessToken, {
            "Content-Type": "application/json",
            ...(canPostMeetingNotesChat && { accept: "application/json" }),
          }),
          body: JSON.stringify(requestBody),
        })
      );

      if (!response.ok) throw new Error("Network response was not ok");

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let partialChunk = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        partialChunk += decoder.decode(value, { stream: true });
        const lines = partialChunk.split("\n");

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const data = processRawData(line);
          if (!data) continue;

          if (
            data.session_id &&
            streamingSessionId &&
            data.session_id !== streamingSessionId &&
            isValidUUID(String(data.session_id))
          ) {
            const fromSessionId: string = streamingSessionId;
            const toSessionId: string = String(data.session_id);
            dispatch(migrateSessionMessages({ fromSessionId, toSessionId }));
            streamingSessionId = toSessionId;
            if (
              shouldPromoteStreamingSession(
                getState,
                fromSessionId,
                toSessionId
              )
            ) {
              dispatch(setSessionId(toSessionId));
              syncHomeUrlToSessionId(toSessionId);
            }
            if (data.session_name) {
              dispatch(
                renameThread({
                  session_id: toSessionId,
                  session_name: data.session_name,
                })
              );
            }
          }

          switch (data.type) {
            case "reasoning":
              if (data.content && data.content !== "json") {
                dispatch(
                  appendToBotMessage({
                    key: botKey,
                    thought: data.content,
                    type: data.type,
                    sessionId: streamingSessionId,
                  })
                );
              }
              break;

            case "response": {
              if (isMeetingNotesStream) {
                const piece =
                  typeof data.content === "string" &&
                  data.content &&
                  data.content !== "json"
                    ? data.content
                    : extractAssistantTextFromJson(data);
                if (piece) {
                  dispatch(
                    appendToBotMessage({
                      key: botKey,
                      question: piece,
                      type: "meeting_notes_chat",
                      sessionId: streamingSessionId,
                    })
                  );
                }
              } else if (data.content && data.content !== "json") {
                dispatch(
                  appendToBotMessage({
                    key: botKey,
                    question: data.content,
                    type: data.type,
                    sessionId: streamingSessionId,
                  })
                );
              }
              break;
            }

            case "greeting":
              if (data.content && data.content !== "json") {
                dispatch(
                  appendToBotMessage({
                    key: botKey,
                    greeting: data.content,
                    type: data.type,
                    sessionId: streamingSessionId,
                  })
                );
              }
              break;

            case "complete": {
              dispatch(
                markMessageComplete({
                  key: botKey,
                  images: data.image_path,
                  messageId: data.message_id,
                  sessionId: streamingSessionId,
                })
              );

              const { user } = getState();
              if (user?.accessToken && user?.email) {
                setTimeout(() => {
                  dispatch(
                    fetchChatThreads({
                      accessToken: user.accessToken!,
                      useremail: user.email!,
                    })
                  );
                }, 1000);
              }
              break;
            }

            case "document_search": {
              dispatch(
                setDocumentSearchResults({
                  key: botKey,
                  results: data.results,
                  messageId: data.message_id,
                  sessionId: streamingSessionId,
                })
              );
              break;
            }

            case "followup_questions": {
              const questions = normalizeFollowUpQuestions(data.questions);
              if (questions.length > 0) {
                dispatch(
                  setFollowUpQuestions({
                    key: botKey,
                    questions,
                    messageId: data.message_id,
                    sessionId: streamingSessionId,
                  })
                );
              }
              break;
            }

            default:
              break;
          }
        }

        partialChunk = lines[lines.length - 1];
      }

      reader.releaseLock();
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Unknown error in chat request");
    }
  }
);

/* ---------------------- Collection chat (NEW) ---------------------- */

async function postJsonStream(url: string, accessToken: string, body: any) {
  const resp = await fetch(
    url,
    getClientFetchInit(accessToken, {
      method: "POST",
      headers: getClientAuthHeaders(accessToken, {
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(body),
    })
  );
  if (!resp.ok) {
    let errorMessage = `POST ${url} failed: ${resp.status}`;
    try {
      const errorText = await resp.text();
      if (errorText) {
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch {
          errorMessage = `${errorMessage} - ${errorText}`;
        }
      }
    } catch {
      // If we can't read the error, use the default message
    }
    throw new Error(errorMessage);
  }
  return resp;
}

export const onCollectionChat = createAsyncThunk<
  void,
  {
    accessToken: string;
    query: string;
    collectionIds: (string | { id?: string; name?: string })[];
    newChat: boolean;
    sessionIdOverride?: string;
    clientMessageKeys?: { userKey?: string; botKey?: string };
  },
  { state: RootState }
>(
  "chat/collection-chat",
  async (
    {
      accessToken,
      query,
      collectionIds,
      newChat,
      sessionIdOverride,
      clientMessageKeys,
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const currentSessionId = resolveChatSessionIdForSubmit({
        sessionIdOverride: sessionIdOverride ?? null,
        reduxSessionId: getState().chat.sessionId,
      });
      if (getState().chat.sessionId !== currentSessionId) {
        dispatch(setSessionId(currentSessionId));
      }

      let streamingSessionId: string = currentSessionId;

      const st0 = getState();
      let selectedRowKeys = getStoredCollectionFileIds(streamingSessionId);
      const fileMap = st0.chat.sessionCollectionFileIds;
      const hasFileKey =
        fileMap != null &&
        Object.prototype.hasOwnProperty.call(fileMap, streamingSessionId);
      if (hasFileKey) {
        const fromStore = fileMap[streamingSessionId];
        const fromRedux = Array.isArray(fromStore)
          ? fromStore.filter(
              (id: unknown): id is string =>
                typeof id === "string" && id.trim() !== ""
            )
          : [];
        if (fromRedux.length > 0) {
          selectedRowKeys = fromRedux;
        }
      }
      selectedRowKeys = Array.from(
        new Set(
          selectedRowKeys
            .map((id) => String(id).trim())
            .filter((id) => id.length > 0)
        )
      );

      const userKey =
        clientMessageKeys?.userKey?.trim() || Date.now().toString();
      const botKey =
        clientMessageKeys?.botKey?.trim() || `${userKey}-bot`;
      const state = getState();
      const collectionSnapshot = resolveCollectionChatSnapshot(
        currentSessionId,
        state.chat
      );

      seedChatTurnIfNeeded(dispatch, getState().chat.messages, {
        userKey,
        botKey,
        sessionId: currentSessionId,
        userInput: query,
        userExtras: {
          collectionNames: collectionSnapshot.displayNames,
          collectionLabel: collectionSnapshot.label,
          ...(collectionSnapshot.contextNames?.length
            ? { collectionContextNames: collectionSnapshot.contextNames }
            : {}),
          isCollectionChat: true,
          type: "collection_chat",
        },
        botExtras: {
          isCollectionChat: true,
          type: "collection_chat",
        },
      });

      await new Promise((r) => setTimeout(r, 10));

      // Ensure collectionIds is an array of strings (not objects)
      const normalizedCollectionIds = Array.isArray(collectionIds)
        ? collectionIds.map((id): string => {
            if (typeof id === "string") return id;
            if (id && typeof id === "object" && "id" in id && id.id) {
              return id.id;
            }
            // Fallback: try to get id from object, or use empty string (will be filtered out)
            return (id as any)?.id || "";
          }).filter((id): id is string => id.length > 0)
        : [];

      // Validate that we have at least one collection ID
      if (normalizedCollectionIds.length === 0) {
        throw new Error("At least one collection ID is required");
      }

      const body: any = {
        query,
        collection_ids: normalizedCollectionIds,
        session_id: streamingSessionId,
        // Only include collection_file_ids if it has values
        ...(Array.isArray(selectedRowKeys) && selectedRowKeys.length > 0
          ? { collection_file_ids: selectedRowKeys }
          : {}),
      };
      const resp = await postJsonStream(
        collectionChatApiUrl,
        accessToken,
        body
      );
      const reader = resp.body!.getReader();
      const decoder = new TextDecoder();
      let partialChunk = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        partialChunk += decoder.decode(value, { stream: true });
        const lines = partialChunk.split("\n");

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const data = processRawData(line);
          if (!data) continue;

          if (
            data.session_id &&
            streamingSessionId &&
            data.session_id !== streamingSessionId &&
            isValidUUID(String(data.session_id))
          ) {
            const fromSessionId: string = streamingSessionId;
            const toSessionId: string = String(data.session_id);
            dispatch(migrateSessionMessages({ fromSessionId, toSessionId }));
            streamingSessionId = toSessionId;
            if (
              shouldPromoteStreamingSession(
                getState,
                fromSessionId,
                toSessionId
              )
            ) {
              dispatch(setSessionId(toSessionId));
              syncHomeUrlToSessionId(toSessionId);
            }
          }

          switch (data.type) {
            case "reasoning":
              if (data.content && data.content !== "json") {
                dispatch(
                  appendToBotMessage({
                    key: botKey,
                    thought: data.content,
                    type: "collection_chat",
                    sessionId: streamingSessionId,
                  })
                );
              }
              break;

            case "response":
              if (data.content && data.content !== "json") {
                dispatch(
                  appendToBotMessage({
                    key: botKey,
                    question: data.content,
                    type: "collection_chat",
                    sessionId: streamingSessionId,
                  })
                );
              }
              break;

            case "greeting":
              if (data.content && data.content !== "json") {
                dispatch(
                  appendToBotMessage({
                    key: botKey,
                    greeting: data.content,
                    type: "collection_chat",
                    sessionId: streamingSessionId,
                  })
                );
              }
              break;

            case "document_search": {
              dispatch(
                setDocumentSearchResults({
                  key: botKey,
                  results: data.results,
                  messageId: data.message_id,
                  sessionId: streamingSessionId,
                })
              );
              break;
            }

            case "complete": {
              dispatch(
                markMessageComplete({
                  key: botKey,
                  images: data.image_path,
                  messageId: data.message_id,
                  sessionId: streamingSessionId,
                })
              );

              const { user } = getState();
              if (user?.accessToken && user?.email) {
                setTimeout(() => {
                  dispatch(
                    fetchChatThreads({
                      accessToken: user.accessToken!,
                      useremail: user.email!,
                    })
                  );
                }, 1000);
              }
              break;
            }

            case "followup_questions": {
              const questions = normalizeFollowUpQuestions(data.questions);
              if (questions.length > 0) {
                dispatch(
                  setFollowUpQuestions({
                    key: botKey,
                    questions,
                    messageId: data.message_id,
                    sessionId: streamingSessionId,
                  })
                );
              }
              break;
            }

            default:
              break;
          }
        }

        partialChunk = lines[lines.length - 1];
      }

      reader.releaseLock();
    } catch (err: any) {
      return rejectWithValue(
        err?.message ?? "Unknown error in collection chat"
      );
    }
  }
);

/* ---------------------- Employee 360 chat (NEW) ---------------------- */
export const onEmployee360Chat = createAsyncThunk<
  void,
  {
    accessToken: string;
    query: string;
    sessionId: string;
    newChat: boolean;
    clientMessageKeys?: { userKey?: string; botKey?: string };
  },
  { state: RootState }
>(
  "chat/employee-360-chat",
  async (
    { accessToken, query, sessionId, newChat, clientMessageKeys },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      let currentSessionId = resolveChatSessionIdForSubmit({
        sessionIdOverride: sessionId,
        reduxSessionId: getState().chat.sessionId,
      });
      if (getState().chat.sessionId !== currentSessionId) {
        dispatch(setSessionId(currentSessionId));
      }

      const userKey =
        clientMessageKeys?.userKey?.trim() || Date.now().toString();
      const botKey =
        clientMessageKeys?.botKey?.trim() || `${userKey}-bot`;

      seedChatTurnIfNeeded(dispatch, getState().chat.messages, {
        userKey,
        botKey,
        sessionId: currentSessionId,
        userInput: query,
        userExtras: {
          type: "employee_360",
        },
        botExtras: {
          type: "employee_360",
        },
      });

      await new Promise((r) => setTimeout(r, 10));

      const body = {
        query,
        session_id: currentSessionId,
      };

      const resp = await postJsonStream(employee360ApiUrl, accessToken, body);

      const reader = resp.body!.getReader();
      const decoder = new TextDecoder();
      let partialChunk = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        partialChunk += decoder.decode(value, { stream: true });
        const lines = partialChunk.split("\n");

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const data = processRawData(line);
          if (!data) continue;

          if (
            data.session_id &&
            currentSessionId &&
            data.session_id !== currentSessionId &&
            isValidUUID(String(data.session_id))
          ) {
            const fromSessionId: string = currentSessionId;
            const toSessionId: string = String(data.session_id);
            dispatch(migrateSessionMessages({ fromSessionId, toSessionId }));
            currentSessionId = toSessionId;
            if (
              shouldPromoteStreamingSession(
                getState,
                fromSessionId,
                toSessionId
              )
            ) {
              dispatch(setSessionId(toSessionId));
              syncHomeUrlToSessionId(toSessionId);
            }
          }

          switch (data.type) {
            case "reasoning":
              if (data.content && data.content !== "json") {
                dispatch(
                  appendToBotMessage({
                    key: botKey,
                    thought: data.content,
                    type: "employee_360",
                    sessionId: currentSessionId,
                  })
                );
              }
              break;

            case "response":
              if (data.content && data.content !== "json") {
                dispatch(
                  appendToBotMessage({
                    key: botKey,
                    question: data.content,
                    type: "employee_360",
                    sessionId: currentSessionId,
                  })
                );
              }
              break;

            case "greeting":
              if (data.content && data.content !== "json") {
                dispatch(
                  appendToBotMessage({
                    key: botKey,
                    greeting: data.content,
                    type: "employee_360",
                    sessionId: currentSessionId,
                  })
                );
              }
              break;

            case "document_search": {
              dispatch(
                setDocumentSearchResults({
                  key: botKey,
                  results: data.results,
                  messageId: data.message_id,
                  sessionId: currentSessionId,
                })
              );
              break;
            }

            case "complete": {
              dispatch(
                markMessageComplete({
                  key: botKey,
                  images: data.image_path,
                  messageId: data.message_id,
                  sessionId: currentSessionId,
                })
              );

              const { user } = getState();
              if (user?.accessToken && user?.email) {
                setTimeout(() => {
                  dispatch(
                    fetchChatThreads({
                      accessToken: user.accessToken!,
                      useremail: user.email!,
                    })
                  );
                }, 1000);
              }
              break;
            }

            case "followup_questions": {
              const questions = normalizeFollowUpQuestions(data.questions);
              if (questions.length > 0) {
                dispatch(
                  setFollowUpQuestions({
                    key: botKey,
                    questions,
                    messageId: data.message_id,
                    sessionId: currentSessionId,
                  })
                );
              }
              break;
            }

            default:
              break;
          }
        }

        partialChunk = lines[lines.length - 1];
      }

      reader.releaseLock();
    } catch (err: any) {
      return rejectWithValue(
        err?.message ?? "Unknown error in employee 360 chat"
      );
    }
  }
);

/* ---------------------- Unified chooser (NEW) ---------------------- */
export const sendChat = createAsyncThunk<
  void,
  {
    accessToken: string;
    userInput: string;
    newChat: boolean;
    collectionIds?: string[];
    payload?: any;
  },
  { state: RootState }
>(
  "chat/sendChat",
  async (
    { accessToken, userInput, newChat, collectionIds = [], payload },
    { dispatch }
  ) => {
    const hasCollections =
      Array.isArray(collectionIds) && collectionIds.length > 0;

    if (hasCollections) {
      await dispatch(
        onCollectionChat({
          accessToken,
          query: userInput,
          collectionIds,
          newChat,
        })
      );
    } else {
      await dispatch(
        onTextChat({
          accessToken,
          payload: payload ?? { query: userInput },
          newChat,
          userInput,
        })
      );
    }
  }
);

/* ---------------------- History mapping and fetch ---------------------- */
function mapInteractionsToMessages(
  interactions: any[],
  sessionId?: string
): ChatMessage[] {
  const results: ChatMessage[] = [];
  const storedFileItems = getStoredCollectionFileItems(sessionId);
  const storedFileIds = getStoredCollectionFileIds(sessionId);
  const storedFileNames = storedFileItems.map((item) => item.name);
  const hasStoredFileSelection =
    storedFileNames.length > 0 || storedFileIds.length > 0;

  interactions.forEach((interaction, index) => {
    const baseKey = Date.now() + index * 1000;

    // Normalize type - can be string or array
    const interactionType = Array.isArray(interaction?.type)
      ? interaction.type
      : interaction?.type
      ? [interaction.type]
      : [];
    const isSearchAndQna = Array.isArray(interaction?.type) &&
      interaction.type.includes("search") &&
      interaction.type.includes("qna");

    const userEntry: ChatMessage = {
      question: interaction.user_query ?? "",
      key: baseKey.toString(),
      role: "user",
      thought: "",
      messageId: interaction?.id ?? "",
      type: Array.isArray(interaction?.type) ? interaction.type.join(",") : (interaction?.type ?? ""),
      complete: true,
      ...(interaction?.type === "collection_chat" || 
          (Array.isArray(interaction?.type) && interaction.type.includes("collection_chat"))
        ? (() => {
            const apiCollectionNames = Array.isArray(
              interaction?.response?.collection_names
            )
              ? interaction.response.collection_names.filter(Boolean)
              : [];
            const apiFileNames = Array.isArray(
              interaction?.response?.collection_file_names
            )
              ? interaction.response.collection_file_names.filter(Boolean)
              : [];
            const fileDisplayNames =
              storedFileNames.length > 0
                ? storedFileNames
                : apiFileNames.length > 0
                  ? apiFileNames
                  : storedFileIds;
            const isFileLevel =
              hasStoredFileSelection ||
              apiFileNames.length > 0 ||
              storedFileIds.length > 0;
            return {
              collectionNames: isFileLevel
                ? fileDisplayNames
                : apiCollectionNames,
              collectionLabel: isFileLevel ? "Files" : "Collections",
              ...(isFileLevel && apiCollectionNames.length > 0
                ? { collectionContextNames: apiCollectionNames }
                : {}),
            };
          })()
        : {}),
      ...(interaction?.type === "meeting_notes_chat" &&
      typeof interaction?.meeting_name === "string" &&
      interaction.meeting_name.trim()
        ? { meetingName: interaction.meeting_name.trim() }
        : {}),
    };

    let botEntry: ChatMessage = {
      question: "No response available.",
      key: `${baseKey}_bot`,
      role: "bot",
      thought: "",
      complete: true,
      messageId: interaction?.id ?? "",
      type: Array.isArray(interaction?.type) ? interaction.type.join(",") : (interaction?.type ?? ""),
    };

    try {
      // 0) Search + QnA combined branch (NEW - handles type: ["search", "qna"])
      if (isSearchAndQna && interaction?.data) {
        const qnaData = interaction.data?.qna;
        const searchData = interaction.data?.search;
        
        // Extract QnA response - the structure is data.qna.response.llm_response
        // llm_response can be a JSON string or an object containing the actual response
        // Priority: use llm_response if available, otherwise fall back to response object
        const qnaResponseRaw = qnaData?.response?.llm_response || qnaData?.response;
        
        // Use coerceLlm to extract response text, reasoning, and images
        // coerceLlm is designed to handle JSON strings and objects from LLM responses
        const { responseText, reasoning, images } = coerceLlm(qnaResponseRaw);
        
        // The responseText should contain the actual QnA answer
        // If it's empty, it means coerceLlm couldn't extract it, so try manual extraction
        let qnaText = responseText || "";
        
        // If coerceLlm didn't extract text, try manual extraction from llm_response
        if (!qnaText || !qnaText.trim()) {
          const llmResp = qnaData?.response?.llm_response;
          if (llmResp) {
            // If it's a JSON string, parse it
            if (typeof llmResp === "string" && (llmResp.trim().startsWith("{") || llmResp.trim().startsWith("["))) {
              try {
                const parsed = JSON.parse(llmResp);
                qnaText = parsed.response || parsed.results || parsed.text || parsed.content || parsed.answer || parsed.message || "";
              } catch {
                // Not valid JSON, use as-is if it's a reasonable length
                qnaText = llmResp.length < 10000 ? llmResp : "";
              }
            } else if (typeof llmResp === "string") {
              // Plain string
              qnaText = llmResp;
            } else if (typeof llmResp === "object") {
              // Object - try common fields
              qnaText = llmResp.response || llmResp.results || llmResp.text || llmResp.content || llmResp.answer || llmResp.message || "";
            }
          }
        }
        
        // Extract search results
        const searchResponse = searchData?.response || {};
        const resultsArray = Array.isArray(searchResponse?.results)
          ? searchResponse.results
          : Array.isArray(searchResponse?.results?.results)
          ? searchResponse.results.results
          : [];

        // Ensure QnA response text is not empty - use a meaningful default
        const finalQnaText = (qnaText && qnaText.trim()) 
          ? qnaText.trim() 
          : "Here's what I found.";

        botEntry = {
          ...botEntry,
          question: finalQnaText, // Always show QnA response first
          thought: reasoning,
          images,
          complete: true,
          // Include search results for document display (shown after QnA)
          showDocumentSelection: resultsArray.length > 0,
          searchResults: {
            ...searchResponse,
            result_count: searchResponse?.result_count ?? resultsArray.length,
          },
          documentSearchResults: resultsArray,
          type: "search,qna", // Combined type
        };
        
        console.log("[mapInteractionsToMessages] Mapped search+qna interaction:", {
          interactionId: interaction?.id,
          qnaStatus: qnaData?.status,
          searchStatus: searchData?.status,
          qnaResponseStructure: {
            hasResponse: !!qnaData?.response,
            hasLlmResponse: !!qnaData?.response?.llm_response,
            llmResponseType: typeof qnaData?.response?.llm_response,
            llmResponsePreview: typeof qnaData?.response?.llm_response === "string" 
              ? qnaData.response.llm_response.substring(0, 100) 
              : qnaData?.response?.llm_response,
          },
          extractedQnaText: qnaText?.substring(0, 200),
          finalQnaText: finalQnaText?.substring(0, 200),
          resultCount: searchResponse?.result_count ?? resultsArray.length,
          hasQnaResponse: !!qnaText && qnaText.trim().length > 0,
          hasSearchResults: resultsArray.length > 0,
        });
      }
      // 1a) Standalone search with data.search structure
      else if (
        (interactionType.includes("search") && !interactionType.includes("qna")) &&
        interaction?.data?.search
      ) {
        const searchData = interaction.data.search;
        const searchResponse = searchData?.response || {};
        const resultsArray = Array.isArray(searchResponse?.results)
          ? searchResponse.results
          : Array.isArray(searchResponse?.results?.results)
          ? searchResponse.results.results
          : [];

        botEntry = {
          ...botEntry,
          question: `Found ${
            searchResponse?.result_count ?? resultsArray.length
          } documents related to "${
            interaction.user_query
          }". Please select documents to analyze.`,
          showDocumentSelection: resultsArray.length > 0,
          searchResults: {
            ...searchResponse,
            result_count: searchResponse?.result_count ?? resultsArray.length,
          },
          documentSearchResults: resultsArray,
          complete: searchData?.status === "success",
        };
        
        console.log("[mapInteractionsToMessages] Mapped standalone search interaction:", {
          interactionId: interaction?.id,
          searchStatus: searchData?.status,
          resultCount: searchResponse?.result_count ?? resultsArray.length,
        });
      }
      // 1b) Document search branch (legacy format)
      else if (interaction.response?.results?.type === "document_search") {
        const searchResponse = interaction.response;
        const resultsArray = Array.isArray(searchResponse?.results?.results)
          ? searchResponse.results.results
          : [];

        botEntry = {
          ...botEntry,
          question: `Found ${
            searchResponse?.result_count ?? 0
          } documents related to "${
            interaction.user_query
          }". Please select documents to analyze.`,
          showDocumentSelection: true,
          searchResults: searchResponse,
          documentSearchResults: resultsArray,
          thought: searchResponse?.results?.reasoning ?? null,
          complete: true,
        };
      }
      // 2) Greeting branch
      else if (interaction.response?.results?.type === "greeting") {
        botEntry = {
          ...botEntry,
          question: ensureString(
            interaction.response?.results?.results,
            "Hello!"
          ),
          complete: true,
        };
      }
      // 2a) Standalone QnA with data.qna structure
      else if (
        (interactionType.includes("qna") && !interactionType.includes("search")) &&
        interaction?.data?.qna
      ) {
        const qnaData = interaction.data.qna;
        const { responseText, reasoning, images } = coerceLlm(
          qnaData?.response
        );
        
        botEntry = {
          ...botEntry,
          question: responseText || "I don't have anything to show yet.",
          thought: reasoning,
          images,
          complete: qnaData?.status === "success",
        };
        
        console.log("[mapInteractionsToMessages] Mapped standalone qna interaction:", {
          interactionId: interaction?.id,
          qnaStatus: qnaData?.status,
          hasResponse: !!responseText,
        });
      }
      // 3) collection_chat (SPECIAL CASE)
      else if (
        interaction?.type === "collection_chat" ||
        (Array.isArray(interaction?.type) && interaction.type.includes("collection_chat"))
      ) {
        const collectionResponse =
          interaction?.response?.llm_response ??
          interaction?.response?.response ??
          interaction?.response?.results ??
          interaction?.response?.content ??
          interaction?.response?.answer ??
          interaction?.response?.message ??
          interaction?.response?.text ??
          interaction?.response;
        const { responseText, reasoning, images } = coerceLlm(
          collectionResponse
        );
        botEntry = {
          ...botEntry,
          question: responseText || "",
          thought: reasoning,
          images,
          complete: true,
          collectionNames: interaction?.response?.collection_names ?? [],
        };
      }
      // 4) employee_360 (SPECIAL CASE)
      else if (interaction?.type === "employee_360") {
        const { responseText, reasoning, images } = coerceLlm(
          interaction?.response?.llm_response
        );
        // Extract genie_message_id for employee_360 - check multiple possible locations
        const genieMessageId = 
          interaction?.response?.genie_message_id ||
          interaction?.genie_message_id ||
          interaction?.data?.genie_message_id ||
          interaction?.response?.data?.genie_message_id ||
          null;
        
        botEntry = {
          ...botEntry,
          question: responseText || "I don't have anything to show yet.",
          thought: reasoning,
          images,
          complete: true,
          type: "employee_360",
          // Use genie_message_id if available, otherwise fall back to interaction.id
          messageId: genieMessageId || interaction?.id || "",
          // Store genie_message_id separately for feedback API
          message_id: genieMessageId || interaction?.id || "",
          // Store genie_message_id as separate field for explicit access
          genie_message_id: genieMessageId || undefined,
        };
      }

      // 5) generic llm_response (non-collection_chat, non-employee_360)
      else if (interaction?.response?.llm_response != null) {
        const { responseText, reasoning, images } = coerceLlm(
          interaction.response.llm_response
        );
        botEntry = {
          ...botEntry,
          question: responseText || botEntry.question,
          thought: reasoning,
          images,
          complete: true,
        };
        // 🟢 Add this after both userEntry and botEntry exist:
        console.log(
          "[history->map] user snapshot collections:",
          userEntry.collectionNames
        );
        console.log(
          "[history->map] bot snapshot collections:",
          (botEntry as any).collectionNames
        );
      }
    } catch (error) {
      // Final guard — keep UI safe and stringy
      console.error("[mapInteractionsToMessages] Error mapping interaction:", {
        interactionId: interaction?.id,
        type: interaction?.type,
        error: error instanceof Error ? error.message : String(error),
      });
      
      // Try to extract any available response text
      const fallbackText = 
        interaction?.data?.qna?.response 
          ? ensureString(interaction.data.qna.response, botEntry.question)
          : interaction?.response?.llm_response
          ? ensureString(interaction.response.llm_response, botEntry.question)
          : botEntry.question;
      
      botEntry.question = fallbackText;
      botEntry.complete = true;
    }

    const followUpQuestions = extractFollowUpQuestionsFromInteraction(
      interaction as Record<string, unknown>
    );
    if (followUpQuestions.length > 0) {
      botEntry = { ...botEntry, followUpQuestions };
    }

    results.push(userEntry, botEntry);
  });

  return results;
}

export function isValidUUID(uuid: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    uuid
  );
}

/** True for client-side ids created before the backend assigns a persisted session UUID. */
export function isTempChatSessionId(sessionId: string | null | undefined): boolean {
  return typeof sessionId === "string" && sessionId.trim().startsWith("temp-");
}

type ChatSessionThreadHint = Pick<ChatThread, "session_id" | "session_name">;

/** Normalize legacy `temp-{uuid}` bookmarks to the bare session uuid. */
export function resolvePersistedChatSessionId(
  sessionId: string | null | undefined,
  _threads: Record<string, ChatThread> = {},
  _threadHint?: ChatSessionThreadHint | null
): string {
  const trimmed = (typeof sessionId === "string" ? sessionId : "").trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith("temp-")) {
    const bare = trimmed.slice("temp-".length);
    if (isValidUUID(bare)) return bare;
  }
  return trimmed;
}

/** Session id safe for `/home/:id`. */
export function getRoutableChatSessionId(
  sessionId: string | null | undefined,
  threads: Record<string, ChatThread> = {},
  threadHint?: ChatSessionThreadHint | null
): string {
  const resolved = resolvePersistedChatSessionId(sessionId, threads, threadHint);
  if (!resolved) return "";
  if (isValidUUID(resolved)) return resolved;
  if (resolved.startsWith("temp-")) {
    const bare = resolved.slice("temp-".length);
    return isValidUUID(bare) ? bare : "";
  }
  return resolved;
}

function normalizeChatSessionIdForApi(
  sessionId: string | null | undefined
): string {
  const trimmed = (typeof sessionId === "string" ? sessionId : "").trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("temp-")) {
    const bare = trimmed.slice("temp-".length);
    return isValidUUID(bare) ? bare : trimmed;
  }
  return trimmed;
}

/** One session id for submit + Redux keys (always a real uuid, never `temp-*`). */
export function resolveChatSessionIdForSubmit(options: {
  payloadSessionTrim?: string;
  sessionIdOverride?: string | null;
  reduxSessionId?: string | null;
}): string {
  const payloadSessionTrim = normalizeChatSessionIdForApi(
    options.payloadSessionTrim
  );
  const override = normalizeChatSessionIdForApi(options.sessionIdOverride);
  const redux = normalizeChatSessionIdForApi(options.reduxSessionId);

  if (payloadSessionTrim && isValidUUID(payloadSessionTrim)) {
    return payloadSessionTrim;
  }

  for (const candidate of [override, redux, payloadSessionTrim]) {
    if (candidate && isValidUUID(candidate)) return candidate;
  }

  return generateSecureId();
}

/** Returns true if the id looks like a meeting ID (e.g. long base64-like string starting with AAMk) */
export function isMeetingId(id: string): boolean {
  if (!id || typeof id !== "string") return false;
  const trimmed = id.trim();
  return trimmed.length > 50 && (trimmed.startsWith("AAMk") || trimmed.startsWith("AAM"));
}

/**
 * Maps meeting-notes `history` rows (query + llm_response) into chat messages.
 * Used by GET .../meeting/:id/history and by unified session history when the
 * backend puts the same payload under `history` while `interactions` is empty.
 */
export function mapMeetingHistoryEnvelopeToChatMessages(
  envelopeHistory: unknown[],
  sessionId: string
): ChatMessage[] {
  const sid = sessionId?.trim() ?? "";
  if (!sid || !Array.isArray(envelopeHistory)) return [];

  const sortedForSession = filterAndSortMeetingHistoryForSession<any>(
    envelopeHistory,
    sid
  );

  const mappedMessages: ChatMessage[] = [];
  sortedForSession.forEach((item: any, idx: number) => {
    const userText = getMeetingHistoryUserQuery(item);
    const botText = getMeetingHistoryAssistantText(item);
    const meetingTitle = getMeetingHistoryMeetingName(item);
    const baseKey =
      item?.chat_log_id != null ? String(item.chat_log_id) : String(idx);

    if (userText) {
      mappedMessages.push({
        key: `u-${baseKey}-${idx}`,
        role: "user",
        content: userText,
        question: userText,
        type: "meeting_notes_chat",
        complete: true,
        ...(meetingTitle ? { meetingName: meetingTitle } : {}),
      });
    }

    if (botText) {
      mappedMessages.push({
        key: `b-${baseKey}-${idx}`,
        role: "bot",
        content: botText,
        question: botText,
        type: "meeting_notes_chat",
        complete: true,
      });
    }
  });
  return mappedMessages;
}

export const fetchMeetingChatHistory = createAsyncThunk<
  void,
  {
    accessToken: string;
    meetingId: string;
    sessionId?: string;
    /**
     * When true: GET .../meeting/{meetingId}/history?limit=50 without session_id (full meeting
     * payload). Rows are filtered client-side to the active session_id so Recent Chats never
     * mix sessions. When false, session_id is still sent for API hints but mapping always
     * enforces session_id match on each row.
     */
    omitSessionIdFromHistoryQuery?: boolean;
  },
  { state: RootState }
>(
  "chat/fetchMeetingChatHistory",
  async (
    { accessToken, meetingId, sessionId, omitSessionIdFromHistoryQuery },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const sid = sessionId?.trim() ?? "";
      if (!sid || !isValidUUID(sid)) {
        return rejectWithValue(
          "Meeting chat history requires a valid session_id (UUID); use the chat session URL, not the meeting id."
        );
      }

      const params: Record<string, string | number> = { limit: 50 };
      if (!omitSessionIdFromHistoryQuery) {
        params.session_id = sid;
      }

      const response = await axios.get(getMeetingChatHistoryUrl(meetingId), {
        headers: getClientAuthHeaders(accessToken, {
          "Content-Type": "application/json",
        }),
        withCredentials: true,
        params,
      });

      if (response.status !== 200) throw new Error("Failed to fetch meeting chat history");

      const { history: rawHistory } = normalizeMeetingChatHistoryEnvelope(
        response.data
      );

      const extractedIds = extractMeetingIdsFromHistoryForSession(
        rawHistory,
        sid
      );
      if (extractedIds.length > 0) {
        console.log("[Meeting ID from History]", extractedIds[0]);
        dispatch(
          mergeThreadMeetingMetadata({
            sessionId: sid,
            meeting_id: extractedIds[0],
            meeting_ids: extractedIds,
          })
        );
        persistSessionMeetingEntry(sid, {
          meeting_id: extractedIds[0],
          meeting_ids: extractedIds,
          type: "meeting_notes_chat",
          source: "teams_ai_assist",
          chatType: "teams_ai_assist",
        });
      }

      const mappedMessages = mapMeetingHistoryEnvelopeToChatMessages(
        rawHistory,
        sid
      );

      const threadMeetingTitle = mappedMessages.find(
        (m) =>
          m.role === "user" &&
          typeof m.meetingName === "string" &&
          m.meetingName.trim()
      )?.meetingName;
      if (typeof threadMeetingTitle === "string" && threadMeetingTitle.trim()) {
        dispatch(
          mergeThreadMeetingMetadata({
            sessionId: sid,
            meeting_name: threadMeetingTitle.trim(),
          })
        );
      }

      console.log("[MeetingHistory] raw history length:", rawHistory.length);
      console.log(
        "[MeetingHistory] session-filtered mapped length:",
        mappedMessages.length
      );

      /**
       * Backend has write→read lag for freshly streamed Teams AI Assist turns. A refetch can return
       * fewer rows than what we already mirrored to Redux (e.g. Q1+A1 indexed, Q2+A2 not yet).
       * Replacing unconditionally drops the unindexed turns and they vanish from Recent Chats →
       * /home/:id until the next refetch sees them. Treat the server payload as authoritative only
       * when it covers at least what we already have; otherwise keep the local in-memory transcript.
       */
      const existing = getState().chat.messages?.[sid];
      const existingCount = Array.isArray(existing) ? existing.length : 0;
      if (mappedMessages.length >= existingCount) {
        dispatch(
          setSessionMessages({
            sessionId: sid,
            messages: mappedMessages,
          })
        );
      }
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to fetch meeting chat history");
    }
  }
);

const MEETING_HISTORY_PROBE_LIMIT = 12;

/**
 * Load chat transcript for /home/:sessionId — picks meeting-notes vs unified history,
 * persists meeting links, and probes meeting history when metadata was missing (Teams → browser).
 */
export const resolveAndLoadSessionHistory = createAsyncThunk<
  void,
  {
    accessToken: string;
    sessionId: string;
    useremail?: string;
    forceRefetch?: boolean;
  },
  { state: RootState }
>(
  "chat/resolveAndLoadSessionHistory",
  async (
    { accessToken, sessionId, useremail, forceRefetch = false },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const sid = sessionId.trim();
      if (!sid || isTempChatSessionId(sid) || !isValidUUID(sid)) {
        return;
      }

      const existingCount = messageCountForSession(getState().chat.messages, sid);
      if (!forceRefetch && existingCount > 0) {
        return;
      }

      if (useremail) {
        try {
          await dispatch(
            fetchChatThreads({ accessToken, useremail })
          ).unwrap();
        } catch (e) {
          console.warn("[resolveAndLoadSessionHistory] fetchChatThreads failed", e);
        }
      }

      let threads = getState().chat.threads;
      let thread = getThreadFromStoreBySessionId(threads, sid);
      let meetingIds = resolveMeetingIdsForSession(threads, sid);

      const tryMeetingHistory = async (): Promise<number> => {
        if (meetingIds.length === 0) return 0;
        await dispatch(
          fetchMeetingChatHistory({
            accessToken,
            meetingId: meetingIds[0],
            sessionId: sid,
            omitSessionIdFromHistoryQuery: true,
          })
        );
        return messageCountForSession(getState().chat.messages, sid);
      };

      const shouldPreferMeetingApi =
        meetingIds.length > 0 || isMeetingAssistThread(thread);

      if (shouldPreferMeetingApi && meetingIds.length > 0) {
        const count = await tryMeetingHistory();
        if (count > 0) {
          persistThreadMeetingLink(sid, getThreadFromStoreBySessionId(getState().chat.threads, sid));
          return;
        }
      }

      try {
        await dispatch(
          fetchChatHistoryFromSessionApi({ accessToken, sessionId: sid })
        ).unwrap();
      } catch (e) {
        console.warn("[resolveAndLoadSessionHistory] unified history failed", e);
      }

      threads = getState().chat.threads;
      thread = getThreadFromStoreBySessionId(threads, sid);
      meetingIds = resolveMeetingIdsForSession(threads, sid);

      let afterUnified = messageCountForSession(getState().chat.messages, sid);

      if (afterUnified > 0) {
        persistThreadMeetingLink(sid, thread);
        return;
      }

      if (meetingIds.length > 0) {
        afterUnified = await tryMeetingHistory();
        if (afterUnified > 0) {
          persistThreadMeetingLink(sid, getThreadFromStoreBySessionId(getState().chat.threads, sid));
          return;
        }
      }

      const storedEntry = getSessionMeetingEntry(sid);
      const shouldProbe =
        isMeetingAssistThread(thread) ||
        storedEntry?.chatType === "teams_ai_assist" ||
        storedEntry?.type === "meeting_notes_chat" ||
        storedEntry?.type === "meeting_notes";

      if (shouldProbe) {
        const candidates = collectMeetingIdsFromThreads(threads);
        for (const mid of candidates.slice(0, MEETING_HISTORY_PROBE_LIMIT)) {
          if (meetingIds.includes(mid)) continue;
          await dispatch(
            fetchMeetingChatHistory({
              accessToken,
              meetingId: mid,
              sessionId: sid,
              omitSessionIdFromHistoryQuery: true,
            })
          );
          const count = messageCountForSession(getState().chat.messages, sid);
          if (count > 0) {
            persistSessionMeetingEntry(sid, {
              meeting_id: mid,
              meeting_ids: [mid],
              type: "meeting_notes_chat",
              source: "teams_ai_assist",
              chatType: "teams_ai_assist",
            });
            return;
          }
        }
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load session history";
      return rejectWithValue(message);
    }
  }
);

/** Stream meeting-notes chat on the home screen — same backend session as Teams AI Assist. */
export const onMeetingNotesChat = createAsyncThunk<
  void,
  {
    accessToken: string;
    meetingId: string;
    /** When set, sent as meeting_ids (e.g. multi-meeting AI Assist); otherwise [meetingId] */
    meetingIds?: string[];
    sessionId: string;
    userInput: string;
    meetingName?: string;
    clientMessageKeys?: { userKey?: string; botKey?: string };
  },
  { state: RootState }
>(
  "chat/meeting-notes-chat",
  async (
    {
      accessToken,
      meetingId,
      meetingIds,
      sessionId,
      userInput,
      meetingName,
      clientMessageKeys,
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      let currentSessionId = String(sessionId || "").trim();
      if (!currentSessionId) {
        return rejectWithValue("Missing session id for meeting chat");
      }
      if (!isValidUUID(currentSessionId)) {
        return rejectWithValue(
          "Meeting notes chat requires a valid session id — open this chat from Recent Chats (session URL), not the meeting id."
        );
      }

      const userKey =
        clientMessageKeys?.userKey?.trim() || Date.now().toString();
      const botKey =
        clientMessageKeys?.botKey?.trim() || `${userKey}-bot`;

      const meetingNameTrim =
        typeof meetingName === "string" && meetingName.trim()
          ? meetingName.trim()
          : undefined;

      seedChatTurnIfNeeded(dispatch, getState().chat.messages, {
        userKey,
        botKey,
        sessionId: currentSessionId,
        userInput,
        userExtras: {
          type: "meeting_notes_chat",
          ...(meetingNameTrim ? { meetingName: meetingNameTrim } : {}),
        },
        botExtras: {
          type: "meeting_notes_chat",
        },
      });

      await new Promise((r) => setTimeout(r, 10));

      const idsPayload =
        Array.isArray(meetingIds) && meetingIds.length > 0
          ? normalizeMeetingIdsList(meetingIds)
          : [String(meetingId).trim()].filter(Boolean);

      const response = await fetch(
        chatMeetingNotesChatApiUrl,
        getClientFetchInit(accessToken, {
          method: "POST",
          headers: getClientAuthHeaders(accessToken, {
            "Content-Type": "application/json",
            accept: "application/json",
          }),
          body: JSON.stringify({
            meeting_ids: idsPayload,
            query: userInput.trim(),
            session_id: currentSessionId,
            type: "meeting_notes_chat",
          }),
        })
      );

      if (!response.ok) {
        let errorMessage = `Meeting notes chat failed: ${response.status}`;
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errJson = JSON.parse(errorText);
              errorMessage =
                errJson.error || errJson.message || errorMessage;
            } catch {
              errorMessage = `${errorMessage} - ${errorText}`;
            }
          }
        } catch {
          /* ignore */
        }
        throw new Error(errorMessage);
      }

      if (!response.body) throw new Error("Response body is null");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let partialChunk = "";
      let threadsRefreshScheduled = false;

      const handleDataObject = (data: any) => {
        if (!data) return;

        if (
          data.session_id &&
          currentSessionId &&
          data.session_id !== currentSessionId &&
          isValidUUID(String(data.session_id))
        ) {
          const fromSessionId = currentSessionId;
          const toSessionId = String(data.session_id);
          dispatch(migrateSessionMessages({ fromSessionId, toSessionId }));
          currentSessionId = toSessionId;
          if (
            shouldPromoteStreamingSession(
              getState,
              fromSessionId,
              toSessionId
            )
          ) {
            dispatch(setSessionId(toSessionId));
            syncHomeUrlToSessionId(toSessionId);
          }
        }

        if (data.type === "response") {
          const piece =
            typeof data.content === "string" && data.content
              ? data.content
              : extractAssistantTextFromJson(data);
          if (piece) {
            dispatch(
              appendToBotMessage({
                key: botKey,
                question: piece,
                sessionId: currentSessionId,
                type: "meeting_notes_chat",
              })
            );
          }
        }

        if (data.type === "complete" && !threadsRefreshScheduled) {
          threadsRefreshScheduled = true;
          dispatch(
            markMessageComplete({
              key: botKey,
              images: data.image_path,
              messageId: data.message_id,
              sessionId: currentSessionId,
            })
          );
          const { user } = getState();
          if (user?.accessToken && user?.email) {
            setTimeout(() => {
              dispatch(
                fetchChatThreads({
                  accessToken: user.accessToken!,
                  useremail: user.email!,
                })
              );
            }, 1000);
          }
        }

        if (data.type === "followup_questions") {
          const questions = normalizeFollowUpQuestions(data.questions);
          if (questions.length > 0) {
            dispatch(
              setFollowUpQuestions({
                key: botKey,
                questions,
                messageId: data.message_id,
                sessionId: currentSessionId,
              })
            );
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        partialChunk += decoder.decode(value, { stream: true });
        const lines = partialChunk.split("\n");

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          const data = processRawData(line);
          if (!data) continue;
          handleDataObject(data);
        }

        partialChunk = lines[lines.length - 1];
      }

      const tail = partialChunk.trim();
      if (tail) {
        const data = processRawData(tail);
        if (data) handleDataObject(data);
      }

      reader.releaseLock();
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Meeting notes chat failed");
    }
  }
);

function buildCollectionPairs(
  interactions: any[]
): { id: string; name: string }[] {
  const pairs: { id: string; name: string }[] = [];
  const seen = new Set<string>();

  // Process interactions in reverse chronological order (latest first)
  const sortedInteractions = [...interactions]
    .filter((inter: any) => inter?.type === "collection_chat")
    .sort((a: any, b: any) => {
      const timeA = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA; // Latest first
    });

  for (const inter of sortedInteractions) {
    const resp = inter?.response ?? {};
    
    // Extract collections_used - can be at response level or nested in llm_response
    const ids: any[] = Array.isArray(resp.collections_used)
      ? resp.collections_used
      : Array.isArray(resp?.llm_response?.collections_used)
      ? resp.llm_response.collections_used
      : [];

    // Extract collection_names - should be at response level
    const names: any[] = Array.isArray(resp.collection_names)
      ? resp.collection_names
      : [];

    console.log("[buildCollectionPairs] Processing interaction:", {
      interactionId: inter?.id,
      collections_used: ids,
      collection_names: names,
    });

    // Pair IDs with names by index
    const n = Math.max(ids.length, names.length);
    for (let i = 0; i < n; i++) {
      const rawId = ids[i];
      const rawName = names[i];

      // Normalize ID - handle string, object with id property, or undefined
      const id = typeof rawId === "string" 
        ? rawId.trim() 
        : rawId?.id 
        ? String(rawId.id).trim()
        : rawId 
        ? String(rawId).trim()
        : "";

      // Normalize name - handle string, object with name property, or fallback to ID
      const name =
        typeof rawName === "string"
          ? rawName.trim()
          : rawName?.name
          ? String(rawName.name).trim()
          : rawName
          ? String(rawName).trim()
          : id || "";

      // Skip if both are empty
      if (!id && !name) continue;

      // Use ID as key if available, otherwise use name
      const key = id || `name:${name}`;
      if (seen.has(key)) continue;

      // Create pair - prefer ID if available, otherwise use name for both
      pairs.push({ 
        id: id || name, 
        name: name || id 
      });
      seen.add(key);
    }
  }

  console.log("[buildCollectionPairs] Final pairs:", pairs);
  return pairs;
}

export const fetchChatHistoryFromSessionApi = createAsyncThunk<
  void,
  { accessToken: string; sessionId: string },
  { state: RootState }
>(
  "chat/fetchChatHistoryFromSessionApi",
  async ({ accessToken, sessionId }, { dispatch, getState, rejectWithValue }) => {
    try {
      if (!isValidUUID(sessionId)) throw new Error("Invalid sessionId format");

      const response = await axios.get(fetchChatHistoryFromSession(sessionId), {
        headers: getClientAuthHeaders(accessToken, {
          "Content-Type": "application/json",
        }),
        withCredentials: true,
      });

      if (response.status !== 200)
        throw new Error("Failed to fetch session history");

      const data = response.data;
      console.log("[fetchChatHistoryFromSessionApi] Raw API response:", data);

      const interactions = Array.isArray(data.interactions)
        ? data.interactions
        : [];

      const { history: envelopeHistory } =
        normalizeMeetingChatHistoryEnvelope(data);
      let extractedFromHistory = extractMeetingIdsFromSessionHistoryPayload(
        data,
        sessionId
      );
      if (extractedFromHistory.length === 0) {
        extractedFromHistory = extractMeetingIdsFromInteractions(interactions);
      }
      if (extractedFromHistory.length > 0) {
        console.log("[Meeting ID from History]", extractedFromHistory[0]);
        dispatch(
          mergeThreadMeetingMetadata({
            sessionId,
            meeting_id: extractedFromHistory[0],
            meeting_ids: extractedFromHistory,
          })
        );
        persistSessionMeetingEntry(sessionId, {
          meeting_id: extractedFromHistory[0],
          meeting_ids: extractedFromHistory,
          type: "meeting_notes_chat",
          source: "teams_ai_assist",
          chatType: "teams_ai_assist",
        });
      }

      const meetingNameFromHistory = interactions.find(
        (inter: { type?: string; meeting_name?: string }) =>
          inter?.type === "meeting_notes_chat" &&
          typeof inter?.meeting_name === "string" &&
          inter.meeting_name.trim()
      )?.meeting_name;
      if (typeof meetingNameFromHistory === "string" && meetingNameFromHistory.trim()) {
        dispatch(
          mergeThreadMeetingMetadata({
            sessionId,
            meeting_name: meetingNameFromHistory.trim(),
          })
        );
      }

      console.log("[fetchChatHistoryFromSessionApi] Interactions:", interactions.map((inter: any) => ({
        id: inter?.id,
        type: inter?.type,
        has_response: !!inter?.response,
        has_data: !!inter?.data,
        data_keys: inter?.data ? Object.keys(inter.data) : [],
        collections_used: inter?.response?.collections_used,
        collection_names: inter?.response?.collection_names,
        search_result_count: inter?.data?.search?.response?.result_count,
        qna_status: inter?.data?.qna?.status,
        search_status: inter?.data?.search?.status,
      })));

      const interactionMessages = mapInteractionsToMessages(interactions, sessionId);
      const fromMeetingHistoryEnvelope = mapMeetingHistoryEnvelopeToChatMessages(
        envelopeHistory,
        sessionId
      );
      const mappedMessages =
        interactionMessages.length > 0
          ? interactionMessages
          : fromMeetingHistoryEnvelope;

      console.log(
        "[fetchChatHistoryFromSessionApi] interactions / meeting-envelope / final mapped lengths:",
        interactions.length,
        fromMeetingHistoryEnvelope.length,
        mappedMessages.length
      );
      const existingMessages = getMessagesForSessionLoose(
        getState().chat.messages,
        sessionId
      );

      if (mappedMessages.length === 0) {
        console.warn(
          "[fetchChatHistoryFromSessionApi] Skipping setSessionMessages (empty map — preserving existing messages)",
          { sessionId }
        );
      } else if (shouldPreserveLocalMessages(existingMessages, mappedMessages)) {
        console.warn(
          "[fetchChatHistoryFromSessionApi] Skipping stale/empty history overwrite — preserving local streamed messages",
          { sessionId }
        );
      } else {
        dispatch(
          setSessionMessages({
            sessionId,
            messages: mappedMessages,
          })
        );
      }

      // ✅ Extract {id,name} pairs directly from the raw interactions
      const pairs = buildCollectionPairs(interactions);

      console.log("[fetchChatHistoryFromSessionApi] Extracted collection pairs:", pairs);
      console.log("[fetchChatHistoryFromSessionApi] Storing in Redux for sessionId:", sessionId);

      // Store collection history without wiping current unsent file selections for this session.
      dispatch(setSessionCollections({ sessionId, collections: pairs, mode: "replace" }));

      console.log("[fetchChatHistoryFromSessionApi] Successfully stored collections in Redux");
    } catch (err: any) {
      return rejectWithValue(err?.message || "Failed to fetch session history");
    }
  }
);

/* ---------------------- Zscaler helper functions ---------------------- */

/**
 * Extracts numeric status code from status or statusText
 * Handles formats like: 403, "403", "403 IP Forbidden", etc.
 */
export function extractStatusCode(status: number | string | undefined | null): number | null {
  if (typeof status === "number") {
    return status;
  }
  if (typeof status === "string") {
    // Try to extract number from string (e.g., "403 IP Forbidden" -> 403)
    const match = status.match(/^(\d+)/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (!isNaN(num)) {
        return num;
      }
    }
  }
  return null;
}

/**
 * Zscaler popup message constant
 */
export const ZSCAR_MESSAGE = "Your Zscaler policy needs to be updated. Please contact your administrator or update your Zscaler settings.";

/**
 * Checks if response body indicates Zscaler issue and triggers popup if needed
 */
export function triggerZscarPopupIfNeeded(
  responseBody: any,
  dispatch: AppDispatch,
  httpStatus?: number,
  httpStatusText?: string
): void {
  const statusFromBody =
    responseBody?.status_code ??
    responseBody?.statusCode ??
    responseBody?.error?.status_code ??
    responseBody?.error?.statusCode;

  const inferredFromBody =
    typeof statusFromBody === "number"
      ? statusFromBody
      : extractStatusCode(statusFromBody);

  const inferredStatus =
    httpStatus ?? inferredFromBody ?? null;

  const statusText =
    httpStatusText ??
    responseBody?.status_text ??
    responseBody?.statusText ??
    "";

  if (
    inferredStatus != null &&
    !Number.isNaN(Number(inferredStatus)) &&
    tryActivateSiteDisabled403(Number(inferredStatus), statusText, responseBody)
  ) {
    return;
  }

  if (!responseBody) {
    return;
  }

  // Check for status_code in various possible locations
  const statusCode =
    responseBody.status_code ??
    responseBody.statusCode ??
    responseBody.error?.status_code ??
    responseBody.error?.statusCode;

  // Check for Zscaler/network-related error messages
  const errorMessage =
    responseBody.message ??
    responseBody.error?.message ??
    responseBody.error_message ??
    "";

  const normalizedErrorMessage =
    typeof errorMessage === "string" ? errorMessage.toLowerCase() : "";

  const isNetworkError =
    normalizedErrorMessage.includes("network error") ||
    normalizedErrorMessage.includes("failed to fetch") ||
    normalizedErrorMessage.includes("typeerror");

  const isZscalerError =
    statusCode === 403 ||
    statusCode === 401 ||
    statusCode === 0 ||
    isNetworkError ||
    normalizedErrorMessage.includes("zscaler") ||
    normalizedErrorMessage.includes("ip forbidden") ||
    normalizedErrorMessage.includes("forbidden");

  if (isZscalerError) {
    dispatch(showPopup(ZSCAR_MESSAGE));
  }
}
